import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { translations, TranslationKey } from '../i18n/translations';

type Lang = 'he' | 'en';

type LanguageContextType = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey) => string;
  dir: 'rtl' | 'ltr';
  isHebrew: boolean;
};

const LanguageContext = createContext<LanguageContextType>({
  lang: 'he',
  setLang: () => {},
  t: (key) => key,
  dir: 'rtl',
  isHebrew: true,
});

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    return (localStorage.getItem('gh_lang') as Lang) || 'he';
  });

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    localStorage.setItem('gh_lang', newLang);
  };

  useEffect(() => {
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  const dir: 'rtl' | 'ltr' = lang === 'he' ? 'rtl' : 'ltr';
  const isHebrew = lang === 'he';

  const t = (key: TranslationKey): string => {
    return translations[lang][key] || translations['he'][key] || key;
  };

  return <LanguageContext.Provider value={{ lang, setLang, t, dir, isHebrew }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
